import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import { isAdminRole } from '../../../../Shared/constants/userRoles';
import { SHARED_CONSTANTS } from '../../../../Shared/constants/commonConstants';
import { ApiError } from '../../../../Shared/interfaces/ApiError';
import useApiClient from '../../../../Shared/services/api';
import { useAppContext } from '../../../../Shared/store/appContext';
import { editBooking, useBookingDetails, useCreateBooking } from '../../../services/helpers';
import { updateContextualNodes } from '../../../services/service';
import { EndpointQueryKeys } from '../../../constants/endpoints';
import { createButtonConfig } from '../config/buttonConfig';
import {
    BookingStep, DetailedFormType, TYPE_OF_PURCHASE, GTM_STATUS
} from '../constants/DetailedBookingConstants';
import { getModalTitle } from '../constants/FormFieldsConstants';
import { ContextualNode, BookingFormData } from '../interface/BookingFormInterface';
import {
    BookingMode, CreateBookingModalProps, DetailedBookingIMPData, DetailedBookingSOVData,
    IMPContextualNodeData, SlotEntryData, SOVContextualNodeData, SlotPackage
} from '../interface/DetailedBookingInterface';
import {
    getChangedEditableFields, hasEditableFieldsChanged,
    mapBookingResponseToFormData, resolveEditErrorMessage
} from '../services/helpers';
import { NON_CONTEXTUAL_PACKAGE_NAME } from '../../../constants/formConstants';

type DetailedFormData = DetailedBookingIMPData | DetailedBookingSOVData;
type ContextualNodeData = IMPContextualNodeData[] | SOVContextualNodeData[];

export const useBookingDataOperations = ({
    isOpen,
    onClose,
    editBookingId,
}: CreateBookingModalProps) => {
    const { t } = useTranslation();
    const api = useApiClient();
    const { state } = useAppContext();
    const queryClient = useQueryClient();
    const artEventId = state.selectedArtEventId ?? '';
    const currentUserRole = state.currentUser?.role ?? '';

    const { EDIT: EDITMODE, CREATE: CREATEMODE } = BookingMode;
    const formMode = !!editBookingId ? EDITMODE : CREATEMODE;

    const [currentStep, setCurrentStep] = useState<BookingStep>(
        BookingStep.INITIAL
    );
    const [detailedFormType, setDetailedFormType] =
        useState<DetailedFormType | null>(null);
    const [validateFormTrigger, setValidateFormTrigger] =
        useState<boolean>(false);
    const [validateDetailedFormTrigger, setValidateDetailedFormTrigger] =
        useState<boolean>(false);
    const [isCreatingBooking, setIsCreatingBooking] = useState<boolean>(false);
    const [bookingError, setBookingError] = useState<{
        error: ApiError;
        customMessage?: string;
    } | null>(null);
    const [bookingId, setBookingId] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [hasNavigatedToSecondPage, setHasNavigatedToSecondPage] =
        useState<boolean>(false);
    const [existingContextualNodes, setExistingContextualNodes] = useState<
        ContextualNode[] | null
    >(null);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [entryId, setEntryId] = useState<number>(0);
    const [resetDetailedFormErrors, setResetDetailedFormErrors] = useState<boolean>(false);
    const [currentFormData, setCurrentFormData] = // current form data that gets UPDATED
        useState<BookingFormData | null>(null);
    const [originalBookingData, setOriginalBookingData] = // the initial form data for comparison
        useState<BookingFormData | null>(null);
    const processedEditBookingIdRef = useRef<string | null>(null);
    const [filteredSlotPackages, setFilteredSlotPackages] = useState<SlotPackage[] | null>(null);

    // Set when the user clicks "Send for GTM Review" so the submit handler can
    // inject gtmStatus = SENT_FOR_GTM_REVIEW into the edit payload.
    const sendForGtmReviewRef = useRef<boolean>(false);

    const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const createBookingMutation = useCreateBooking(api, artEventId);

    const shouldEnterEditMode = formMode === EDITMODE;
    const bookingDetailsQuery = useBookingDetails(
        api,
        editBookingId || '',
        shouldEnterEditMode && isOpen && !!editBookingId
    );

    const initialStateSetUp = () => {
        setCurrentStep(BookingStep.INITIAL);
        setDetailedFormType(null);
        setValidateFormTrigger(false);
        setValidateDetailedFormTrigger(false);
        setIsCreatingBooking(false);
        setBookingError(null);
        setHasNavigatedToSecondPage(false);
        setCurrentFormData(null);
        setBookingId(null);
        setIsEditMode(false);
        setResetDetailedFormErrors(false);
        setExistingContextualNodes(null);
        setFilteredSlotPackages(null);
        setSuccessMessage('');
        setOriginalBookingData(null);
    };

    useEffect(() => {
        if (isOpen) {
            initialStateSetUp();
            const shouldEnterEditMode = formMode === EDITMODE;

            if (
                shouldEnterEditMode &&
                editBookingId &&
                editBookingId.trim() !== '' &&
                processedEditBookingIdRef.current !== editBookingId
            ) processedEditBookingIdRef.current = editBookingId;
        } else {
            processedEditBookingIdRef.current = null;
            initialStateSetUp();

            queryClient.removeQueries({
                predicate: (query) => {
                    const [queryKey] = query.queryKey;
                    return queryKey === EndpointQueryKeys.GET_SLOT_PACKAGES;
                },
            });
        }
    }, [isOpen, editBookingId, formMode]);

    useEffect(() => {
        if (bookingDetailsQuery.data && shouldEnterEditMode) {
            const response = bookingDetailsQuery.data;
            if (response && response.statusCode === 200 && response.booking) {
                const {
                    booking: {
                        contextualNodes = [],
                        bookingId = '',
                        typeOfPurchase = '',
                        entryId = 0,
                    },
                } = response;
                const formData = mapBookingResponseToFormData(response.booking);
                setCurrentFormData(formData);
                setOriginalBookingData(formData);

                if (contextualNodes && contextualNodes.length > 0)
                    setExistingContextualNodes(contextualNodes);
                setBookingId(bookingId);
                setEntryId(entryId);
                setIsEditMode(true);

                if (
                    typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV ||
                    typeOfPurchase === DetailedFormType.SOV
                ) setDetailedFormType(DetailedFormType.SOV);
                else if (
                    typeOfPurchase === TYPE_OF_PURCHASE.GP_IMP ||
                    typeOfPurchase === DetailedFormType.IMP
                ) setDetailedFormType(DetailedFormType.IMP);
                else setDetailedFormType(DetailedFormType.IMP);
            } else setBookingError({
                error: response as ApiError,
                customMessage: t('booking-details-error'),
            });
        }
    }, [bookingDetailsQuery.data, shouldEnterEditMode]);

    // Auto-clear success message after DEFAULT TIME
    useEffect(() => {
        if (successMessage) {
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = setTimeout(() => {
                setSuccessMessage('');
                successTimeoutRef.current = null;
            }, SHARED_CONSTANTS.SUCCESS_SNACKBAR_CLEAR_TIME);
        }

        return () => {
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = null;
        };
    }, [successMessage]);

    const refetchBookingsUponCreation = () => {
        queryClient.refetchQueries({
            predicate: (query) => {
                const [queryKey, queryArtEventId] = query.queryKey;
                return (
                    queryKey === EndpointQueryKeys.GET_BOOKING_SHEET &&
                    queryArtEventId === artEventId
                );
            },
        });
        return;
    };

    const handleFormSubmit = async (formData: BookingFormData) => {
        const { filteredSlotPackages: filteredPackages, ...cleanFormData } = formData;

        if (!isEditMode) {
            try {
                setIsCreatingBooking(true);
                setBookingError(null);

                const response = await createBookingMutation.mutateAsync(cleanFormData);
                const {
                    bookingId: receivedBookingId = '',
                    entryId: receivedEntryId = 0,
                } = response || {};
                if (response) {
                    !!receivedBookingId && setBookingId(receivedBookingId);
                    !!receivedEntryId && setEntryId(receivedEntryId);
                }

                setCurrentFormData(cleanFormData);
                setOriginalBookingData(cleanFormData);
                setHasNavigatedToSecondPage(true);

                if (filteredPackages) {
                    setFilteredSlotPackages(filteredPackages);
                }

                if (cleanFormData.typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV)
                    setDetailedFormType(DetailedFormType.SOV);
                else if (cleanFormData.typeOfPurchase === TYPE_OF_PURCHASE.GP_IMP)
                    setDetailedFormType(DetailedFormType.IMP);
                else setDetailedFormType(DetailedFormType.IMP);

                setCurrentStep(BookingStep.DETAILED);
                setHasNavigatedToSecondPage(true);
                setValidateFormTrigger(false);
                setResetDetailedFormErrors(true);
                refetchBookingsUponCreation();
            } catch (error) {
                setBookingError({
                    error: error as ApiError,
                    customMessage: t('booking-creation-failed'),
                });
            } finally {
                setIsCreatingBooking(false);
            }
        } else {
            try {
                setBookingError(null);

                if (!bookingId) throw new Error(t('booking-id-is-required'));
                setCurrentFormData(cleanFormData);
                setHasNavigatedToSecondPage(true);

                if (filteredPackages) {
                    setFilteredSlotPackages(filteredPackages);
                }

                if (cleanFormData.typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV)
                    setDetailedFormType(DetailedFormType.SOV);
                else if (cleanFormData.typeOfPurchase === TYPE_OF_PURCHASE.GP_IMP)
                    setDetailedFormType(DetailedFormType.IMP);
                else setDetailedFormType(DetailedFormType.IMP);

                setCurrentStep(BookingStep.DETAILED);
                setHasNavigatedToSecondPage(true);
                setValidateFormTrigger(false);
                setResetDetailedFormErrors(true);
            } catch (error) {
                setBookingError({
                    error: error as ApiError,
                    customMessage: t('booking-details-edit-failure'),
                });
            }
        }
    };

    const buildContextualNodesFromSlotEntry = (
        slotEntry: SlotEntryData
    ): ContextualNode[] => {
        const { formData, contextualNodes, detailedFormType } = slotEntry;
        const isIMP = detailedFormType === DetailedFormType.IMP;

        const slotName = isIMP
            ? (formData as DetailedBookingIMPData).slotForGP || ''
            : (formData as DetailedBookingSOVData).gpSlot || '';

        const validNodes = contextualNodes.filter((node) => {
            const value = isIMP
                ? (node as IMPContextualNodeData).imSegment
                : (node as SOVContextualNodeData).contextualNode;
            return value?.trim();
        });

        if (validNodes.length > 0) {
            return validNodes.map((node) => {
                const impNode = node as IMPContextualNodeData;
                const sovNode = node as SOVContextualNodeData;
                return {
                    contextualNodeId: 0,
                    slotOrPackageName: slotName,
                    contextualNode: isIMP ? impNode.imSegment || '' : sovNode.contextualNode || '',
                    percentageBooked: isIMP ? impNode.approxShareBlockedOverNode || 0 : sovNode.sovPercentageForContextualGP || 0,
                    valueBlockedAtPackageLevel: isIMP ? impNode.valueToBeLockedAtPackageLevel || 0 : sovNode.valueBooked || 0,
                    impressionsBooked: isIMP ? impNode.impressions || 0 : sovNode.impressionBooked || 0,
                };
            });
        }

        const impFormData = formData as DetailedBookingIMPData;
        const sovFormData = formData as DetailedBookingSOVData;
        return [{
            contextualNodeId: 0,
            slotOrPackageName: NON_CONTEXTUAL_PACKAGE_NAME.toUpperCase(),
            contextualNode: isIMP ? slotName : sovFormData.gpSlot,
            percentageBooked: isIMP ? 0 : sovFormData.sovPercentageForGP || 0,
            valueBlockedAtPackageLevel: isIMP ? impFormData.valueBlockedByLineItem || 0 : sovFormData.valueBlockedByLineItem || 0,
            impressionsBooked: isIMP ? impFormData.impressionsBlockedOverContextualNode || 0 : sovFormData.impressionsForNonContextual || 0,
        }];
    };

    const handleAllSlotsSubmit = async (allSlotEntries: SlotEntryData[]) => {
        try {
            setIsCreatingBooking(true);
            setBookingError(null);

            if (!bookingId) throw new Error(t('booking-id-is-required'));

            let allContextualNodes: ContextualNode[] = [];
            let nodeIdCounter = 1;

            for (const slotEntry of allSlotEntries) {
                const pageNodes = buildContextualNodesFromSlotEntry(slotEntry);
                for (const node of pageNodes) {
                    allContextualNodes.push({
                        ...node,
                        contextualNodeId: nodeIdCounter++,
                    });
                }
            }

            if (allContextualNodes.length === 0)
                throw new Error(t('one-slot-details-required'));

            const isSendForGtmReview = sendForGtmReviewRef.current;

            const shouldEditBookingWithChanges =
                isEditMode &&
                currentFormData &&
                originalBookingData &&
                (hasEditableFieldsChanged(
                    currentFormData,
                    originalBookingData,
                    currentUserRole
                ) || isSendForGtmReview);

            if (shouldEditBookingWithChanges) {
                const editableFormData = getChangedEditableFields(
                    currentFormData,
                    originalBookingData,
                    currentUserRole
                );

                const combinedPayload = {
                    ...editableFormData,
                    ...(isSendForGtmReview && { gtmStatus: GTM_STATUS.SENT_FOR_GTM_REVIEW }),
                    contextualNodes: allContextualNodes,
                };

                await editBooking(api, bookingId, combinedPayload);
            } else await updateContextualNodes(api, bookingId, allContextualNodes);

            refetchBookingsUponCreation();
            onClose();

            if (isEditMode) setSuccessMessage(t('booking-edited-successfully'));
            else setSuccessMessage(t('booking-created-successfully'));
        } catch (error) {
            setBookingError({
                error: error as ApiError,
                customMessage: resolveEditErrorMessage(error as ApiError, t),
            });
        } finally {
            setIsCreatingBooking(false);
            sendForGtmReviewRef.current = false;
        }
    };

    const handleNextClick = () => setValidateFormTrigger((prev) => !prev);
    const handleCloseSnackBar = () => setSuccessMessage('');
    const handleSubmitClick = () => {
        sendForGtmReviewRef.current = false;
        setValidateDetailedFormTrigger((prev) => !prev);
    };

    // "Send for GTM Review" reuses the normal page-2 submit flow, but flags the
    // submission so the GTM transition is injected into the edit payload.
    const handleSendForGtmReviewClick = () => {
        sendForGtmReviewRef.current = true;
        setValidateDetailedFormTrigger((prev) => !prev);
    };

    // AE/AM can send their own GTM_NOT_READY booking back for review.
    const isGtmReviewButtonVisible =
        isEditMode &&
        currentStep === BookingStep.DETAILED &&
        !isAdminRole(currentUserRole) &&
        originalBookingData?.gtmStatus === GTM_STATUS.GTM_NOT_READY;

    const handlePreviousClick = () => {
        setCurrentStep(BookingStep.INITIAL);
        if (bookingError) setBookingError(null);
        setSuccessMessage('');
        if (hasNavigatedToSecondPage) setIsEditMode(true);
    };

    const modalHeader = useMemo(() => {
        return getModalTitle(formMode, currentStep, detailedFormType, t);
    }, [currentStep, formMode, detailedFormType]);
    const isLoading =
        (shouldEnterEditMode ? bookingDetailsQuery.isLoading : false) ||
        createBookingMutation.isPending ||
        isCreatingBooking;

    const rawCombinedError =
        bookingError ||
        (shouldEnterEditMode ? bookingDetailsQuery.error : null) ||
        createBookingMutation.error;

    const combinedError = bookingError?.error || rawCombinedError;
    const combinedCustomMessage = bookingError?.customMessage;

    return {
        currentStep,
        modalHeader,
        successMessage,
        handleCloseSnackBar,
        buttonConfig: createButtonConfig(
            onClose,
            handleNextClick,
            handlePreviousClick,
            handleSubmitClick,
            !isLoading && !combinedError,
            {
                visible: isGtmReviewButtonVisible,
                onClick: handleSendForGtmReviewClick,
                disabled: isLoading || !!combinedError,
            }
        ),
        formRenderProps: {
            isCreatingBooking: isLoading,
            bookingFormData: currentFormData || originalBookingData,
            handleFormSubmit,
            handleAllSlotsSubmit,
            validateFormTrigger,
            validateDetailedFormTrigger,
            bookingError: combinedError as ApiError | null,
            bookingErrorCustomMessage: combinedCustomMessage,
            currentStep,
            detailedFormType,
            bookingId,
            entryId,
            editBookingId,
            hasNavigatedToSecondPage,
            isEditMode,
            formMode,
            existingContextualNodes,
            resetDetailedFormErrors,
            setResetDetailedFormErrors,
            filteredSlotPackages,
        },
    };
};
